import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useReactFlow } from "reactflow";
import { ArrowCircleRightIcon, BanIcon } from "@heroicons/react/outline";
import { useFormikContext } from "formik";
import { FlowData } from "@hilos/types/flow";
import SelectorField from "src/components/Form/SelectorField";
import { hasItems } from "src/helpers/utils";
import { STEP_TYPES } from "../../../constants/steps";
import useFlowBuilderStore from "../../../hooks/useFlowBuilderStore";
import { SelectedItemState } from "../../../hooks/useFlowLayout";

interface FlowBuilderStepGoToProps {
  id: string;
  index: number;
  onSelectItem?: (nextSelectedItem: SelectedItemState) => void;
}

function formatOptionLabel(data) {
  const RenderIcon =
    (data.type && STEP_TYPES[data.type] && STEP_TYPES[data.type].icon) ||
    BanIcon;

  return (
    <div className="flex flex-nowrap -ml-4 py-1 flex-row justify-start items-center">
      <RenderIcon aria-hidden="true" className="h-4 w-4" />
      <div className="ml-2 font-medium text-sm capitalize">{data.label}</div>
    </div>
  );
}

function FlowBuilderStepGoTo({
  id,
  index,
  onSelectItem,
}: FlowBuilderStepGoToProps) {
  const [t] = useTranslation();
  const { values } = useFormikContext<FlowData>();
  const setHighlightedNodeId = useFlowBuilderStore(
    (state) => state.setHighlightedNodeId
  );
  const { getNodes } = useReactFlow();

  const options = useMemo(() => {
    if (!hasItems(values.steps)) {
      return [];
    }

    return values.steps.reduce((nextOptions, step) => {
      if (
        step.id !== id &&
        step.step_type !== "GO_TO" &&
        step.next_step_default !== id &&
        step.next_step_alternate !== id &&
        step.answer_failed_next_step !== id &&
        (!hasItems(step.next_steps_for_options) ||
          !step.next_steps_for_options.includes(id))
      ) {
        nextOptions.push({
          value: step.id,
          label: step.name,
          type: step.step_type,
        });
      }
      return nextOptions;
    }, [] as any[]);
  }, [values, id]);

  const destinationStepId = values.steps[index]?.next_step_default;

  const handleJumpToDestination = useCallback(() => {
    if (!destinationStepId || !onSelectItem) return;

    const destinationNode = getNodes().find(
      (node) => node.id === destinationStepId
    );
    if (destinationNode) {
      onSelectItem(destinationNode);
    }
  }, [destinationStepId, getNodes, onSelectItem]);

  useEffect(() => {
    setHighlightedNodeId(values.steps[index]?.next_step_default);
  }, [destinationStepId, setHighlightedNodeId, index, values.steps]);

  return (
    <div className="space-y-4">
      <SelectorField
        name={`steps.${index}.next_step_default`}
        label={t(
          "flows:steps.go-to.next-step-default.label",
          "Select a step available to go"
        )}
        options={options}
        formatOptionLabel={formatOptionLabel}
      />
      {destinationStepId && (
        <button
          type="button"
          className="inline-flex rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:text-gray-100"
          onClick={handleJumpToDestination}
        >
          {t("flows:go-to.jump-to-step", "Jump to this step")}
          <ArrowCircleRightIcon className="ml-2 h-5 w-5" />
        </button>
      )}
    </div>
  );
}

export default FlowBuilderStepGoTo;
